import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PolicyAcceptanceModal from './PolicyAcceptanceModal';
import AccountDisabled from './AccountDisabled';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const MARKETPLACE_ACCOUNT_TYPE = 'marketplace';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isAuthenticated, policiesAccepted, acceptPolicies, accountBlocked } = useAuth();
  const location = useLocation();
  const [isAcceptingPolicies, setIsAcceptingPolicies] = useState(false);

  // Marketplace accounts share the session cookie but must never reach the
  // enterprise app — bounce them to their own surface (mirrors the backend
  // guard that 403s marketplace accounts on enterprise resource routes).
  if (isAuthenticated && user?.account_type === MARKETPLACE_ACCOUNT_TYPE) {
    return <Navigate to="/marketplace" replace />;
  }

  // Free-tier access disabled — wall off the authenticated app (public routes,
  // which are not wrapped in ProtectedRoute, stay reachable).
  if (accountBlocked) {
    return <AccountDisabled />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!policiesAccepted) {
    const handleAcceptPolicies = async () => {
      setIsAcceptingPolicies(true);
      try {
        await acceptPolicies();
      } finally {
        setIsAcceptingPolicies(false);
      }
    };

    return <PolicyAcceptanceModal onAccept={handleAcceptPolicies} isLoading={isAcceptingPolicies} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
